import React, { useState } from 'react';
import { Eye, EyeOff, Dumbbell, Mail, Lock, Building, MapPin, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { signupUser } from '../services/authService';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showErrorToast } from '../services/toastService';
export default function SignupPage() {
    const [form, setForm] = useState({
        gymName: '',
        email: '',
        password: '',
        confirmPassword: '',
        address: '',
        mobile: ''
    });
    const { login } = useAuth();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const { token, user } = await signupUser(form);
            await login(token, user);
            navigate('/members');
        } catch {
            showErrorToast('Signup failed');
        } finally {
            setIsLoading(false);
        }
    };
    const nextStep = () => {
        setCurrentStep(2);
    };

    const prevStep = () => {
        setCurrentStep(1);
    };

    const isStep1Valid = form.gymName && form.email && form.password && form.confirmPassword;
    const isStep2Valid = form.address && form.mobile;

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-gray-900 to-black">
            {/* Animated background elements */}
            <div className="absolute inset-0">
                <div className="absolute top-20 left-20 w-72 h-72 bg-amber-600/15 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-600/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-slate-600/10 rounded-full blur-2xl animate-pulse delay-500"></div>
            </div>

            {/* Gym equipment silhouettes */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-10 text-6xl">🏋️</div>
                <div className="absolute top-3/4 right-10 text-6xl">💪</div>
                <div className="absolute bottom-1/4 left-1/4 text-4xl">⚡</div>
                <div className="absolute top-1/2 right-1/4 text-4xl">🔥</div>
            </div>

            {/* Main content */}
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-lg">
                    {/* Signup card with glassmorphism effect */}
                    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                        {/* Header section */}
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-600 to-amber-500 rounded-2xl mb-4 shadow-lg">
                                <Dumbbell className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">
                                Join GymPro
                            </h1>
                            <p className="text-gray-300 text-sm">
                                Create your account and start managing your gym like a pro
                            </p>
                        </div>

                        {/* Progress indicator */}
                        <div className="flex items-center justify-center mb-8">
                            <div className="flex items-center space-x-4">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 1 ? 'bg-amber-500' : 'bg-gray-600'} text-white text-sm font-semibold transition-colors`}>
                                    {currentStep > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                                </div>
                                <div className={`w-12 h-1 ${currentStep > 1 ? 'bg-amber-500' : 'bg-gray-600'} transition-colors`}></div>
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 2 ? 'bg-amber-500' : 'bg-gray-600'} text-white text-sm font-semibold transition-colors`}>
                                    2
                                </div>
                            </div>
                        </div>

                        {/* Multi-step form */}
                        <div className="space-y-6">
                            {currentStep === 1 && (
                                <div className="space-y-6">
                                    <h3 className="text-xl font-semibold text-white text-center mb-6">Basic Information</h3>

                                    {/* Gym Name input */}
                                    <div className="relative group">
                                        <Building className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type="text"
                                            name="gymName"
                                            value={form.gymName}
                                            onChange={handleChange}
                                            placeholder="Enter your gym name"
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                    </div>

                                    {/* Email input */}
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            placeholder="Enter your email"
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                    </div>

                                    {/* Password input */}
                                    <div className="relative group">
                                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={form.password}
                                            onChange={handleChange}
                                            placeholder="Create a password"
                                            required
                                            className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    {/* Confirm Password input */}
                                    <div className="relative group">
                                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={form.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="Confirm your password"
                                            required
                                            className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    {/* Next button */}
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        disabled={!isStep1Valid}
                                        className="w-full relative overflow-hidden bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed group border border-amber-500/20"
                                    >
                                        <div className="flex items-center justify-center space-x-2">
                                            <span>Continue</span>
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-500 opacity-0 group-hover:opacity-20 transition-opacity rounded-2xl"></div>
                                    </button>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="space-y-6">
                                    <h3 className="text-xl font-semibold text-white text-center mb-6">Gym Details</h3>

                                    {/* Address input */}
                                    <div className="relative group">
                                        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type="text"
                                            name="address"
                                            value={form.address}
                                            onChange={handleChange}
                                            placeholder="Enter your gym address"
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                    </div>

                                    {/* Mobile input */}
                                    <div className="relative group">
                                        <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                        <input
                                            type="tel"
                                            name="mobile"
                                            value={form.mobile}
                                            onChange={handleChange}
                                            placeholder="Enter your mobile number"
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                        />
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex space-x-4">
                                        <button
                                            type="button"
                                            onClick={prevStep}
                                            className="flex-1 py-4 px-6 bg-white/5 border border-white/10 text-white rounded-2xl font-semibold hover:bg-white/10 transition-all duration-300"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={!isStep2Valid || isLoading}
                                            className="flex-1 relative overflow-hidden bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed group border border-amber-500/20"
                                        >
                                            <div className="flex items-center justify-center space-x-2">
                                                {isLoading ? (
                                                    <>
                                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                        <span>Creating...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>Create Account</span>
                                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                                    </>
                                                )}
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-500 opacity-0 group-hover:opacity-20 transition-opacity rounded-2xl"></div>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="my-8 flex items-center">
                            <div className="flex-1 border-t border-white/10"></div>
                            <span className="px-4 text-gray-400 text-sm">or</span>
                            <div className="flex-1 border-t border-white/10"></div>
                        </div>

                        {/* Login link */}
                        <div className="text-center">
                            <p className="text-gray-400 text-sm">
                                Already have an account?{' '}
                                <Link to="/login" className="text-amber-400 hover:text-amber-300 transition-colors font-semibold">
                                    Sign In
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Additional info */}
                    <div className="text-center mt-8">
                        <p className="text-gray-500 text-xs">
                            By creating an account, you agree to our Terms of Service and Privacy Policy
                        </p>
                    </div>
                </div>
            </div>

            {/* Floating particles effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-2 h-2 bg-white/10 rounded-full animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 2}s`
                        }}
                    ></div>
                ))}
            </div>
        </div>
    );
}
